import { Icon, Tag } from "lucide-react";
import type { ComponentType, HTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

// ─────────────────────────────────────────────────────────────────────────────
// PAGE PRIMITIVES — THE WAY TO LAY OUT A PAGE.
//
// These are the reusable building blocks every page in this project is built
// from: a constrained Container, a Section with tone/rhythm/texture, an
// Eyebrow + SectionHeading, a PageHeader for inner pages, and the StatGrid /
// FeatureGrid / CtaBand content blocks.
//
// AGENTS MUST REUSE THESE instead of inventing a layout. If a page needs a
// shape that is not here, add it to THIS file so the next page can reuse it —
// do not hand-roll a one-off layout in a page. Every component accepts
// `className` and merges it with cn(), so callers can nudge spacing without
// forking the primitive.
//
// All colours come from tokens (globals.css); all text goes through t() in the
// calling page. Nothing here hardcodes a colour or a sentence.
// ─────────────────────────────────────────────────────────────────────────────

/** The shared content width. `as` lets callers render a div, main, etc. */
export function Container({
  as: Tag = 'div',
  className,
  ...props
}: { as?: 'div' | 'main' | 'section' | 'header' | 'footer' | 'nav' } & HTMLAttributes<HTMLElement>) {
  return <Tag className={cn('mx-auto w-full max-w-6xl px-4 sm:px-6', className)} {...props} />
}

type Tone = 'default' | 'tinted' | 'ink'
type Rhythm = 'hero' | 'default' | 'tight'
type Texture = 'grid' | 'dots' | 'noise'

const toneClass: Record<Tone, string> = {
  default: '',
  tinted: 'bg-secondary/40 border-y border-border/60',
  ink: 'bg-brand-ink text-brand-ink-foreground',
}

const rhythmClass: Record<Rhythm, string> = {
  hero: 'py-20 sm:py-28 lg:py-36',
  default: 'py-16 sm:py-24',
  tight: 'py-10 sm:py-14',
}

const textureClass: Record<Texture, string> = {
  grid: 'tex-grid',
  dots: 'tex-dots',
  noise: 'tex-noise',
}

/**
 * A vertical band of the page. `tone` changes the ground, `rhythm` the
 * vertical padding, `texture` overlays a faint pattern. Children are wrapped
 * in a Container automatically.
 */
export function Section({
  tone = 'default',
  rhythm = 'default',
  texture,
  id,
  className,
  children,
}: {
  tone?: Tone
  rhythm?: Rhythm
  texture?: Texture
  id?: string
  className?: string
  children: ReactNode
}) {
  return (
    <section data-upbo-src="src/components/Page.tsx:77"
      id={id}
      className={cn('relative overflow-hidden', toneClass[tone], rhythmClass[rhythm], className)}
    >
      {texture && (
        <div data-upbo-src="src/components/Page.tsx:82"
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-0',
            textureClass[texture],
          )}
        />
      )}
      <Container className="relative">{children}</Container>
    </section>
  )
}

/** The small uppercase kicker above a heading. */
export function Eyebrow({ className, children }: { className?: string; children: ReactNode }) {
  return <p data-upbo-src="src/components/Page.tsx:97" className={cn('eyebrow', className)}>{children}</p>
}

/**
 * A heading block: optional eyebrow, a title, an optional body, and an
 * optional action (a button or link) laid out beside the heading on wider
 * screens.
 */
export function SectionHeading({
  eyebrow,
  title,
  body,
  action,
  align = 'start',
  className,
}: {
  eyebrow?: string
  title: ReactNode
  body?: ReactNode
  action?: ReactNode
  align?: 'start' | 'center'
  className?: string
}) {
  const heading = (
    <>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 data-upbo-src="src/components/Page.tsx:123" className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
      {body && <p data-upbo-src="src/components/Page.tsx:124" className="mt-4 max-w-xl leading-relaxed text-muted-foreground">{body}</p>}
    </>
  )

  if (action) {
    return (
      <div data-upbo-src="src/components/Page.tsx:130"
        className={cn(
          'flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between',
          align === 'center' && 'sm:items-center sm:text-center',
          className,
        )}
      >
        <div data-upbo-src="src/components/Page.tsx:137" className={cn(align === 'center' && 'mx-auto flex flex-col items-center')}>{heading}</div>
        {action}
      </div>
    )
  }

  return (
    <div data-upbo-src="src/components/Page.tsx:144" className={cn(align === 'center' && 'mx-auto flex max-w-2xl flex-col items-center text-center', className)}>
      {heading}
    </div>
  )
}

/**
 * The top of every inner page: a tight section with a bottom hairline, an
 * eyebrow, a large h1, a body, and an optional actions row.
 */
export function PageHeader({
  eyebrow,
  title,
  body,
  actions,
  className,
}: {
  eyebrow?: string
  title: ReactNode
  body?: ReactNode
  actions?: ReactNode
  className?: string
}) {
  return (
    <Section rhythm="tight" className={cn('border-b border-border/60', className)}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h1 data-upbo-src="src/components/Page.tsx:170" className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">{title}</h1>
      {body && <p data-upbo-src="src/components/Page.tsx:171" className="mt-4 max-w-xl leading-relaxed text-muted-foreground">{body}</p>}
      {actions && <div data-upbo-src="src/components/Page.tsx:172" className="mt-8 flex flex-wrap items-center gap-3">{actions}</div>}
    </Section>
  )
}

/** A row of headline numbers, laid out as a hairline grid. */
export function StatGrid({
  items,
  tone = 'default',
  className,
}: {
  items: { value: string; label: string }[]
  tone?: 'default' | 'ink'
  className?: string
}) {
  const ink = tone === 'ink'
  return (
    <div data-upbo-src="src/components/Page.tsx:189" className={cn('hairline-grid grid-cols-2 lg:grid-cols-4', className)}>
      {items.map((item) => (
        <div data-upbo-src="src/components/Page.tsx:191" key={item.label} className={cn('px-5 py-5', ink ? 'bg-brand-ink' : 'bg-background')}>
          <div data-upbo-src="src/components/Page.tsx:192" className="font-display text-3xl font-semibold tabular-nums">{item.value}</div>
          <div data-upbo-src="src/components/Page.tsx:193" className={cn('mt-1 text-sm', ink ? 'text-brand-ink-foreground/60' : 'text-muted-foreground')}>
            {item.label}
          </div>
        </div>
      ))}
    </div>
  )
}

/** A hairline grid of Feature cells. */
export function FeatureGrid({
  cols = 2,
  className,
  children,
}: {
  cols?: 2 | 3 | 4
  className?: string
  children: ReactNode
}) {
  const colsClass = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  }[cols]
  return <div data-upbo-src="src/components/Page.tsx:217" className={cn('hairline-grid', colsClass, className)}>{children}</div>
}

/** One cell of a FeatureGrid: an icon, a title and a body. */
export function Feature({
  icon: Icon,
  title,
  body,
  className,
}: {
  icon: ComponentType<{ className?: string }>
  title: ReactNode
  body: ReactNode
  className?: string
}) {
  return (
    <div data-upbo-src="src/components/Page.tsx:233" className={cn('flex flex-col gap-3 bg-background p-7', className)}>
      <div data-upbo-src="src/components/Page.tsx:234" className="grid size-10 place-items-center rounded-lg bg-accent text-accent-foreground">
        <Icon className="size-5 text-primary" />
      </div>
      <h3 data-upbo-src="src/components/Page.tsx:237" className="font-medium tracking-tight">{title}</h3>
      <p data-upbo-src="src/components/Page.tsx:238" className="text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  )
}

/**
 * A full-width call-to-action band on the brand-ink ground with a grid
 * texture and a soft glow. The primary button inverts the ink ground.
 */
export function CtaBand({
  title,
  body,
  primary,
  secondary,
  className,
}: {
  title: ReactNode
  body?: ReactNode
  primary: { to: string; label: ReactNode }
  secondary?: { to: string; label: ReactNode }
  className?: string
}) {
  return (
    <Section tone="ink" texture="grid" className={className}>
      <div data-upbo-src="src/components/Page.tsx:262" aria-hidden className="glow pointer-events-none absolute -top-24 left-1/2 h-64 w-[36rem] -translate-x-1/2" />
      <div data-upbo-src="src/components/Page.tsx:263" className="relative flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
        <div data-upbo-src="src/components/Page.tsx:264" className="max-w-2xl">
          <h2 data-upbo-src="src/components/Page.tsx:265" className="text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h2>
          {body && <p data-upbo-src="src/components/Page.tsx:266" className="mt-4 max-w-xl leading-relaxed text-brand-ink-foreground/70">{body}</p>}
        </div>
        <div data-upbo-src="src/components/Page.tsx:268" className="flex flex-wrap items-center gap-3">
          <Button asChild className="bg-brand-ink-foreground text-brand-ink hover:bg-brand-ink-foreground/90">
            <Link to={primary.to}>{primary.label}</Link>
          </Button>
          {secondary && (
            <Button
              asChild
              variant="outline"
              className="border-brand-ink-foreground/25 bg-transparent text-brand-ink-foreground hover:bg-brand-ink-foreground/10 hover:text-brand-ink-foreground"
            >
              <Link to={secondary.to}>{secondary.label}</Link>
            </Button>
          )}
        </div>
      </div>
    </Section>
  )
}